import {Injectable} from '@angular/core';
import {BaseFacade} from '../../../../../core/abstracts/base-facade.abstract';
import {BehaviorSubject, catchError, Observable, Subject, tap, throwError} from 'rxjs';
import {Vehicle} from '../model/vehicle.model';
import {VehicleService} from '../service/vehicle.service';
import {VehicleResponseMapper} from '../mapper/vehicle-response.mapper';
import {VehicleCategoryService} from '../../../category/services/vehicle-category.service';
import {VehicleCategoryResponseMapper} from '../../../category/mappers/vehicle-category-response.mapper';
import {VehicleCategory} from '../../../category/models/vehicle-category.model';
import {Brand} from '../../../brand/models/brand.model';
import {VehicleBrandService} from '../../../brand/services/vehicle-brand.service';
import {VehicleBrandResponseMapper} from '../../../brand/mapper/vehicle-brand-response.mapper';
import {VehicleModelService} from '../../../model/service/vehicle-model.service';
import {VehicleModelsResponseMapper} from '../../../model/mapper/vehicle-models-response.mapper';
import {VehicleModel} from '../../../model/model/vehicle-model.model';
import {Owner} from '../../../owners/models/owner.model';
import {VehicleOwnerService} from '../../../owners/services/vehicle-owner-response.service';
import {PageableRequest} from '../../../../../core/models/pagable.model';
import {OwnerSearchFilterRequest} from '../../../owners/models/owner-search-filter-request.model';
import {VehicleOwnersPageableResponseMapper} from '../../../owners/mappers/vehicle-owners-pageable-response.mapper';
import {Page} from '../../../../../core/models/page-response.model';
import {SelectLoadMoreEvent} from '../../../../../shared/components/input/select-input/select-input.component';
import {NewVehicleFormPayload} from '../model/new-vehicle-form.model';
import {StorageSearchFilterRequest} from '../../../storage/model/storage-search-filter-request.model';
import {VehicleStorageService} from '../../../storage/service/vehicle-storage.service';
import {StoragePageableResponseMapper} from '../../../storage/mapper/storage-pageable-response.mapper';
import {VehicleStorage} from '../../../storage/model/storage.model';
import {NewVehicleRequestMapper} from '../mapper/new-vehicle-request.mapper';
import {
  ConfirmDialogService
} from '../../../../../shared/components/modals/confirm-dialog/service/confirm-dialog.service';
import {
  ConfirmDialogConfig,
  ConfirmDialogResult
} from '../../../../../shared/components/modals/confirm-dialog/model/confirm-dialog.model';
import {MessageService, ToastMessageOptions} from 'primeng/api';
import {VehicleReleaseModalResult} from '../../vehicle-release/model/vehicle-release-modal-result.model';
import {VehicleReleaseService} from '../../vehicle-release/service/vehicle.release.service';
import {VehicleReleaseRequestMapper} from '../../vehicle-release/mapper/vehicle-release-request.mapper';
import {ResponseError} from '../../../../../core/models/response-error.model';
import {VehicleAnnotationService} from '../../../annotation/service/vehicle-annotation.service';
import {VehicleAnnotationRequestMapper} from '../../../annotation/mapper/vehicle-annotation-request.mapper';
import {ItemImageDto} from '../model/item-image.model';
import {VehicleResponse} from '../model/vehicle-response.model';
import {MultipartRequest} from '../../../../../core/interfaces/multipart-request.interface';
import {NewVehicleRequest} from '../model/new-vehicle-request.model';
import {AvailablePropertyType} from '../../../property/enum/available-property.type';
import {VehicleAssignKeyRequestMapper} from '../../vehicle-key-assign/mapper/vehicle-assign-key-request.mapper';

@Injectable({providedIn: 'root'})
export class VehicleFacade extends BaseFacade {

  private readonly itemSubject: BehaviorSubject<Vehicle | null> = new BehaviorSubject<Vehicle | null>(null);
  readonly vehicle$ = this.itemSubject.asObservable();

  private readonly vehicleCategoriesSubject: BehaviorSubject<VehicleCategory[]> = new BehaviorSubject<VehicleCategory[]>([]);
  readonly vehicleCategories$ = this.vehicleCategoriesSubject.asObservable();

  private readonly vehicleBrandsSubject: BehaviorSubject<Brand[]> = new BehaviorSubject<Brand[]>([]);
  readonly vehicleBrands$ = this.vehicleBrandsSubject.asObservable();

  private readonly vehicleModelsSubject: BehaviorSubject<VehicleModel[]> = new BehaviorSubject<VehicleModel[]>([]);
  readonly vehicleModels$ = this.vehicleModelsSubject.asObservable();

  private readonly vehicleOwnersSubject: BehaviorSubject<Page<Owner> | null> = new BehaviorSubject<Page<Owner> | null>(null);
  readonly vehicleOwners$ = this.vehicleOwnersSubject.asObservable();

  private readonly vehicleStoragesSubject: BehaviorSubject<Page<VehicleStorage> | null> = new BehaviorSubject<Page<VehicleStorage> | null>(null);
  readonly vehicleStorages$ = this.vehicleStoragesSubject.asObservable();

  private readonly errorSubject = new Subject<ResponseError | null>();
  readonly error$ = this.errorSubject.asObservable();

  private readonly vehicleImagesSubject: BehaviorSubject<ItemImageDto[]> = new BehaviorSubject<ItemImageDto[]>([]);
  readonly vehicleImages$ = this.vehicleImagesSubject.asObservable();

  constructor(
    private readonly vehicleService: VehicleService,
    private readonly vehicleCategoryService: VehicleCategoryService,
    private readonly vehicleResponseMapper: VehicleResponseMapper,
    private readonly vehicleCategoryResponseMapper: VehicleCategoryResponseMapper,
    private readonly vehicleBrandService: VehicleBrandService,
    private readonly vehicleBrandResponseMapper: VehicleBrandResponseMapper,
    private readonly vehicleModelService: VehicleModelService,
    private readonly vehicleModelResponseMapper: VehicleModelsResponseMapper,
    private readonly vehicleOwnersService: VehicleOwnerService,
    private readonly vehicleOwnersPageableResponseMapper: VehicleOwnersPageableResponseMapper,
    private readonly vehicleStorageService: VehicleStorageService,
    private readonly storagePageableResponseMapper: StoragePageableResponseMapper,
    private readonly newVehicleRequestMapper: NewVehicleRequestMapper,
    private readonly confirmDialogService: ConfirmDialogService,
    private readonly messageService: MessageService,
    private readonly releaseVehicleService: VehicleReleaseService,
    private readonly releaseVehicleRequestMapper: VehicleReleaseRequestMapper,
    private readonly vehicleAnnotationService: VehicleAnnotationService,
    private readonly vehicleAnnotationRequestMapper: VehicleAnnotationRequestMapper,
    private readonly vehicleAssignKeyRequestMapper: VehicleAssignKeyRequestMapper
  ) {
    super();
  }

  public loadVehicle(id: string | null): void {
    if (!id) {
      throw new Error(`[${VehicleFacade.name}] Vehicle ID is not present`);
    }

    this.startLoading("Ładowanie danych pojazdu...");
    this.errorSubject.next(null);

    this.vehicleService.getById(id).subscribe({
      next: (vehicleResponse) => {
        const vehicle = this.vehicleResponseMapper.map(vehicleResponse);
        this.itemSubject.next(vehicle);
        this.stopLoading();
      },
      error: (error) => {
        this.errorSubject.next(error);
        this.stopLoading();
      }
    })
  }

  public loadVehicleImages(id: string | number): void {
    this.vehicleService.listImages(id).subscribe({
      next: (images) => {
        this.vehicleImagesSubject.next(images ?? []);
      },
      error: () => {
        this.vehicleImagesSubject.next([]);
      }
    });
  }

  public uploadVehicleImages(id: string | number, files: File[] | FileList | File | null): void {
    const formData = new FormData();
    const normalizedFiles: File[] = Array.isArray(files)
      ? files
      : files && true
        ? Array.from(files as FileList)
        : files
          ? [files as File]
          : [];

    if (normalizedFiles.length === 0) {
      return;
    }

    normalizedFiles.forEach(file => formData.append('files', file, file.name));

    this.vehicleService.uploadImages(id, formData).subscribe({
      next: () => {
        this.loadVehicleImages(id);
      },
      error: () => {
        this.vehicleImagesSubject.next(this.vehicleImagesSubject.value ?? []);
      }
    });
  }

  public deleteVehicleImage(id: string | number, imageId: number | string): void {
    const previous = this.vehicleImagesSubject.value ?? [];
    this.vehicleImagesSubject.next(previous.filter(image => image.id !== Number(imageId)));

    this.vehicleService.deleteImage(id, imageId).subscribe({
      next: () => {
        this.loadVehicleImages(id);
      },
      error: () => {
        this.vehicleImagesSubject.next(previous);
      }
    });
  }

  public fetchVehicleCategories(): void {
    this.vehicleCategoryService.getAll().subscribe({
      next: (vehicleCategoriesResponse) => {
        const vehicleCategories = this.vehicleCategoryResponseMapper.mapArray(vehicleCategoriesResponse);
        this.vehicleCategoriesSubject.next(vehicleCategories);
      },
      error: (error) => {
        console.error(error);
      }
    });
  }

  public fetchVehicleBrands(): void {
    this.vehicleBrandService.getAll().subscribe({
      next: (vehicleBrandsResponse) => {
        const vehicleBrands = this.vehicleBrandResponseMapper.mapArray(vehicleBrandsResponse);
        this.vehicleBrandsSubject.next(vehicleBrands);
      },
      error: (error) => {
        console.error(error);
      }
    });
  }

  public fetchVehicleModels(brandId: number): void {
    this.vehicleModelService.getById(brandId).subscribe({
      next: (vehicleModelsResponse) => {
        const vehicleModels = this.vehicleModelResponseMapper.mapArray(vehicleModelsResponse);
        this.vehicleModelsSubject.next(vehicleModels);
      },
      error: (error) => {
        console.error(error);
      }
    });
  }

  public fetchVehicleOwners(lazyLoadEvent: SelectLoadMoreEvent): void {
    let pagination: PageableRequest = new PageableRequest(lazyLoadEvent.page, lazyLoadEvent.size);
    let filters: OwnerSearchFilterRequest = {name: lazyLoadEvent.query};

    this.vehicleOwnersService.getPage(pagination, filters).subscribe({
      next: (vehicleOwnersResponse) => {
        const vehicleOwners = this.vehicleOwnersPageableResponseMapper.map(vehicleOwnersResponse);
        this.vehicleOwnersSubject.next(vehicleOwners);
      },
      error: (error) => {
        console.error(error);
      }
    });
  }

  public fetchVehicleStorages(lazyLoadEvent: SelectLoadMoreEvent): void {
    let pagination: PageableRequest = new PageableRequest(lazyLoadEvent.page, lazyLoadEvent.size);
    let filters: StorageSearchFilterRequest = {name: lazyLoadEvent.query};

    this.vehicleStorageService.getPage(pagination, filters).subscribe({
      next: (vehicleStoragesResponse) => {
        const vehicleStorages = this.storagePageableResponseMapper.map(vehicleStoragesResponse);
        this.vehicleStoragesSubject.next(vehicleStorages);
      },
      error: (error) => {
        console.error(error);
      }
    });
  }

  public createNewVehicle(newVehicleFormPayload: NewVehicleFormPayload): Observable<VehicleResponse> {
    const payload: MultipartRequest<NewVehicleRequest> = {
      body: this.newVehicleRequestMapper.map(newVehicleFormPayload),
      files: newVehicleFormPayload.files
    };

    this.startLoading();

    return this.vehicleService.create(payload).pipe(
      tap(() => {
        this.stopLoading();
        this.messageService.add({
          summary: 'Dodawanie pojazdu',
          detail: 'Dodano nowy pojazd do magazynu',
          severity: 'success',
          closable: true,
          icon: 'pi pi-car',
        });
      }),
      catchError((error) => {
        this.stopLoading();
        this.errorSubject.next(error);
        this.messageService.add({
          summary: 'Dodawanie pojazdu',
          detail: 'Błąd dodawania nowego pojazdu do magazynu',
          severity: 'error',
          closable: true,
          icon: 'pi pi-car',
        });
        return throwError(() => error);
      })
    );
  }

  public displayVehicleUsageConfirmationDialog(): Observable<ConfirmDialogResult | undefined> {
    const config: ConfirmDialogConfig = {
      header: 'Przebieg pojazdu',
      message: 'Wprowadzono przebieg pojazdu przekraczający limit ostrzegawczy. Upewnij się, że wprowadzona wartość jest prawidłowa.',
      modal: true,
      dismissableMask: true,
      defaultFocus: 'accept',
      rejectButtonProps: {
        label: 'Przebieg nieprawidłowy',
        severity: 'danger',
        outlined: true,
        rounded: true
      },
      acceptButtonProps: {
        label: 'Przebieg prawidłowy',
        severity: 'primary',
        rounded: true
      }
    }

    return this.confirmDialogService.open(config).onClose;
  }

  public releaseVehicle(vehicleId: string, releaseModalPayload: VehicleReleaseModalResult): void {
    const payload = this.releaseVehicleRequestMapper.map(releaseModalPayload);

    this.startLoading();

    this.releaseVehicleService.create({
      vehicleId: vehicleId,
      releasePayload: payload
    })
      .subscribe({
        next: () => {
          this.stopLoading();
          const toast: ToastMessageOptions = {
            summary: 'Wydawanie pojazdu',
            detail: 'Pomyślnie wydano pojazd',
            severity: 'success',
            closable: true,
            icon: 'pi pi-car'
          }
          this.messageService.add(toast);
        },
        error: (error: ResponseError) => {
          this.errorSubject.next(error);
          this.stopLoading();

          let errorToast: ToastMessageOptions;

          if (error.status && error.status === 409) {
            errorToast = {
              summary: 'Wydawanie pojazdu',
              detail: 'Pojazd został już wydany',
              severity: 'warn',
              closable: true,
              icon: 'pi pi-car'
            }
          } else {
            errorToast = {
              summary: 'Wydawanie pojazdu',
              detail: 'Błąd wydawania pojazdu',
              severity: 'error',
              closable: true,
              icon: 'pi pi-car'
            }
          }

          this.messageService.add(errorToast);
        }
      })
  }

  publishAnnotations(vehicleId: string, annotations: string) {
    this.startLoading();

    const payload = this.vehicleAnnotationRequestMapper.map(annotations);

    this.vehicleAnnotationService.update(vehicleId, payload).subscribe({
      next: () => {
        const toast: ToastMessageOptions = {
          summary: 'Notatki pojazdu',
          detail: 'Dodano notatkę do pojazdu',
          severity: 'success',
          closable: true,
          icon: 'pi pi-file-edit'
        }
        this.messageService.add(toast);
      },
      error: (error) => {
        console.error(error);
        const toast: ToastMessageOptions = {
          summary: 'Notatki pojazdu',
          detail: 'Błąd dodawania notatki do pojazdu',
          severity: 'error',
          closable: true,
          icon: 'pi pi-file-edit'
        }
        this.messageService.add(toast);
      }
    });
  }

  public assignKeyNumber(vehicleId: number, keyNumber: number): void {
    this.startLoading();
    const payload = this.vehicleAssignKeyRequestMapper.map(keyNumber);

    this.vehicleService.assignKey(vehicleId, payload).subscribe({
      next: (result: VehicleResponse) => {
        const brand = result.properties.find(p => p.propertyType === AvailablePropertyType.BRAND)?.value;
        const model = result.properties.find(p => p.propertyType === AvailablePropertyType.MODEL)?.value;
        const vehicle = [brand, model].filter(Boolean).join(' ');

        this.messageService.add({
          severity: 'success',
          summary: 'Przypisywanie kluczyka',
          detail: `Pomyślnie przypisano kluczyk nr. ${result.keyNumber}${vehicle ? ' do pojazdu ' + vehicle : ''}`,
        });

        this.loadVehicle(vehicleId.toString());
      },
      error: (error) => {
        this.errorSubject.next(error);
        this.messageService.add({
          severity: 'error',
          summary: 'Przypisywanie kluczyka',
          detail: 'Błąd przypisywania kluczyka',
        });
      },
      complete: () => this.stopLoading()
    });
  }
}
